import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@repo/ui/lib/utils'

const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'border-destructive bg-card text-destructive *:data-[slot=alert-description]:text-destructive *:[svg]:text-current',
        'destructive-solid':
          'border-destructive bg-red-50 text-destructive *:data-[slot=alert-description]:text-destructive dark:bg-red-950 *:[svg]:text-current',
        info: 'border-blue-600 bg-blue-50 text-blue-600 *:data-[slot=alert-description]:text-blue-600 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300 dark:*:data-[slot=alert-description]:text-blue-300',
        success:
          'border-green-600 bg-green-50 text-green-700 *:data-[slot=alert-description]:text-green-700 dark:border-green-400 dark:bg-green-950 dark:text-green-300 dark:*:data-[slot=alert-description]:text-green-300',
        warning:
          'border-amber-500 bg-card text-amber-700 *:data-[slot=alert-description]:text-amber-700 dark:text-amber-300 dark:*:data-[slot=alert-description]:text-amber-300',
      },
    },
  },
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      data-slot="alert"
      role="alert"
      {...props}
    data-sentry-component="Alert" data-sentry-source-file="alert.tsx" />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div className={cn('absolute top-2 right-2', className)} data-slot="alert-action" {...props} data-sentry-component="AlertAction" data-sentry-source-file="alert.tsx" />
  )
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className,
      )}
      data-slot="alert-description"
      {...props}
    data-sentry-component="AlertDescription" data-sentry-source-file="alert.tsx" />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      data-slot="alert-title"
      {...props}
    data-sentry-component="AlertTitle" data-sentry-source-file="alert.tsx" />
  )
}

export { Alert, AlertAction, AlertDescription, AlertTitle }
